import React, { useState, useContext } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import {
    LeafIcon,
    IconDashboard,
    IconQuiz,
    IconBestiary,
    IconBadge,
    IconDuel,
    IconProfile,
    IconSupport,
    IconMessage,
    IconLogout,
    IconSun,
    IconMoon,
    IconCard,
    IconBlitz,
    IconZap,
    IconTarget,
    IconLayers,
    IconGraduationCap,
    IconBookOpen,
    IconUsers,
    IconFlower,
    IconBriefcase,
    IconList
} from '../ui/Icons';
import DropdownMenu from './DropdownMenu';
import WeatherWidget from './WeatherWidget';

const Header = ({
    user,
    userProfile,
    logout,
    theme,
    toggleTheme,
    dueCount = 0,
    achievementsBadge = null,
    unreadBadge = null,
    appVersion = 'v2.0'
}) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [brandClickCount, setBrandClickCount] = useState(0);

    const closeNav = () => setMobileMenuOpen(false);

    // Format number for badge (e.g. 99+)
    const formatCount = (count) => {
        return count > 99 ? '99+' : count;
    };

    const navItems = [
        {
            key: 'dashboard',
            path: '/dashboard',
            label: 'Dashboard',
            icon: IconDashboard,
            requiresAuth: false,
        },
        {
            key: 'bestiary',
            path: '/bestiary',
            label: 'Bestiary',
            icon: IconBestiary,
            requiresAuth: false,
        },
        ...(user && !user.is_guest ? [
            {
                key: 'career',
                path: '/career',
                label: 'Karriere',
                icon: IconGraduationCap,
                requiresAuth: false,
            }
        ] : []),
    ];

    // Quiz Dropdown Items
    const quizMenuItems = user?.is_guest ? [
        {
            key: 'flashcards',
            label: 'Lernkarten (Testen)',
            path: '/quiz?mode=flashcard',
            icon: IconCard,
        },
        { type: 'divider' },
        {
            key: 'register-quiz',
            label: 'Mehr Modi freischalten 🔓',
            path: '/auth?mode=register',
            icon: IconZap,
        },
    ] : [
        {
            key: 'quiz-overview',
            label: 'Quiz-Übersicht',
            path: '/quiz',
            icon: IconQuiz,
        },
        { type: 'divider' },
        {
            key: 'flashcards',
            label: 'Lernkarten',
            path: '/quiz?mode=flashcard',
            icon: IconCard,
        },
        {
            key: 'blitz',
            label: 'Elite-Blitz',
            path: '/quiz?mode=blitz',
            icon: IconBlitz,
        },
        {
            key: 'speed',
            label: 'Speed-Battle',
            path: '/quiz?mode=speed',
            icon: IconZap,
        },
        {
            key: 'pflanze_in_schlau',
            label: 'Botanik-Genie',
            path: '/quiz?mode=pflanze_in_schlau',
            icon: IconGraduationCap,
        },
        {
            key: 'family',
            label: 'Taxonomie-Quiz',
            path: '/quiz?mode=family',
            icon: IconLayers,
        },
        {
            key: 'multiple_choice',
            label: 'Bestimmungs-Quiz',
            path: '/quiz?mode=multiple_choice',
            icon: IconTarget,
        },
        ...(user?.is_staff ? [
            { type: 'divider' },
            {
                key: 'duel',
                label: 'Pflanzenduell',
                path: '/duel',
                icon: IconDuel,
            }
        ] : [])
    ];

    // User Dropdown Items
    const userMenuItems = user?.is_guest ? [
        {
            key: 'register',
            label: 'Konto erstellen',
            path: '/auth?mode=register',
            icon: IconGraduationCap,
        },
        { type: 'divider' },
        {
            key: 'support',
            label: 'Support',
            path: '/support',
            icon: IconSupport,
        },
        { type: 'divider' },
        {
            key: 'logout',
            label: 'Gast-Session beenden',
            onClick: logout,
            icon: IconLogout,
            variant: 'danger',
        },
    ] : [
        {
            key: 'profile',
            label: 'Profil',
            path: '/profile',
            icon: IconProfile,
        },
        { type: 'divider' },
        {
            key: 'sniggle',
            label: 'Sniggeln',
            path: '/sniggle',
            icon: IconMessage,
            badge: unreadBadge > 0 ? formatCount(unreadBadge) : null,
        },
        {
            key: 'friends',
            label: 'Freunde',
            path: '/friends',
            icon: IconUsers,
        },
        {
            key: 'badges',
            label: 'Badges',
            path: '/badges',
            icon: IconBadge,
            badge: achievementsBadge,
        },
        ...(user?.is_staff || user?.is_superuser || userProfile?.is_community_member ? [
            {
                key: 'apprentice',
                label: 'Ausbildungsnachweis',
                path: '/apprentice-journal',
                icon: IconBookOpen,
            },
            {
                key: 'pflanze_der_woche',
                label: 'Pflanze der Woche',
                path: '/apprentice/pflanze-der-woche',
                icon: IconFlower,
            },
            {
                key: 'herbarium',
                label: 'Digitales Herbarium',
                path: '/apprentice/herbarium',
                icon: IconBookOpen,
            },
        ] : []),
        ...(userProfile?.role === 'AUSBILDER' || userProfile?.role === 'BETRIEB' || user?.is_staff ? [
            {
                key: 'coach',
                label: '🎓 Ausbilder-Dashboard',
                path: '/coach',
                icon: IconGraduationCap,
            },
        ] : []),
        ...(userProfile?.is_authorized_employer ? [
            { type: 'divider' },
            {
                key: 'employer_data',
                label: '🏢 Betriebsdaten',
                path: '/career?action=manage_profile',
                icon: IconBriefcase,
            },
            {
                key: 'employer_ads',
                label: '📢 Meine Anzeigen',
                path: '/career',
                icon: IconList,
            },
        ] : []),
        ...(user?.is_staff || user?.is_superuser ? [
            { type: 'divider' },
            {
                key: 'admin_panel',
                label: '⚙️ Admin-Bereich',
                onClick: () => { window.location.href = '/internal-admin-plx/' },
            },
        ] : []),
        { type: 'divider' },
        {
            key: 'support',
            label: 'Support',
            path: '/support',
            icon: IconSupport,
        },
        { type: 'divider' },
        {
            key: 'logout',
            label: 'Abmelden',
            onClick: logout,
            icon: IconLogout,
            variant: 'danger',
        },
    ];

    // Legacy secondary nav items - now empty
    const secondaryNavItems = [];

    const renderNavButton = (item, type = 'desktop') => {
        // Auth Check
        if (item.requiresAuth && user?.is_guest) return null;
        if (item.requiresAdmin && !user?.is_staff && !user?.is_superuser) return null;

        const isActive = location.pathname.startsWith(item.path);

        const baseClass = type === 'desktop'
            ? "relative px-4 py-2 rounded-xl text-sm font-bold transition-all flex items-center gap-2 group focus:outline-none focus:ring-2 focus:ring-plantlix-500/50"
            : "relative w-full px-4 py-3 rounded-xl text-sm font-bold transition flex items-center gap-3";

        const activeClass = type === 'desktop'
            ? "bg-plantlix-500 text-white shadow-lg shadow-plantlix-500/20"
            : "bg-plantlix-500 text-white shadow-md";

        const inactiveClass = type === 'desktop'
            ? "text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-white/10 hover:text-gray-900 dark:hover:text-white"
            : "text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-white/5 hover:text-plantlix-600 dark:hover:text-plantlix-400";

        return (
            <NavLink
                key={item.key}
                to={item.path}
                onClick={closeNav}
                className={({ isActive: linkActive }) =>
                    `${baseClass} ${(isActive || linkActive) ? activeClass : inactiveClass}`
                }
            >
                <item.icon className={type === 'desktop' ? "w-4 h-4" : "w-5 h-5"} />
                <span>{item.label}</span>
                {item.badge && (
                    <span className={`absolute ${type === 'desktop' ? '-top-1 -right-1' : 'top-3 right-4'} flex h-4 w-4 md:h-5 md:w-5 items-center justify-center rounded-full bg-red-500 text-[10px] md:text-xs text-white ring-2 ring-white dark:ring-[#101419]`}>
                        {item.badge}
                    </span>
                )}
            </NavLink>
        );
    };

    return (
        <header className="sticky top-0 z-[2000] bg-white/80 dark:bg-[#0a0a0a]/80 backdrop-blur-xl border-b border-gray-100 dark:border-white/5 text-gray-900 dark:text-gray-100 shadow-sm transition-all duration-300">
            <nav className="container mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-4.5 max-w-full">
                <div className="flex items-center justify-between gap-4 min-w-0">
                    {/* Brandname */}
                    <div
                        className="group flex items-center gap-2.5 sm:gap-3 opacity-90 cursor-pointer"
                        onClick={() => {
                            setBrandClickCount(prev => prev + 1);
                            navigate('/dashboard');
                        }}
                    >
                        <LeafIcon className="w-10 h-10 sm:w-11 sm:h-11 md:w-12 md:h-12 transition-transform group-hover:scale-105" />
                        <div className="flex flex-col">
                            <span className="text-xl sm:text-2xl md:text-3xl font-black bg-gradient-to-br from-plantlix-600 to-lime-500 bg-clip-text text-transparent leading-tight tracking-tighter drop-shadow-sm">
                                Plantlix
                            </span>
                            <span className="text-[0.65rem] sm:text-xs md:text-sm font-bold text-plantlix-700/60 dark:text-plantlix-400/60 leading-tight tracking-widest uppercase">
                                {appVersion?.split('.').length >= 2 ? `${appVersion.split('.')[0]}.${appVersion.split('.')[1]}` : appVersion}
                            </span>
                        </div>
                    </div>

                    {/* Desktop Navigation */}
                    <div className="flex items-center gap-2 flex-1 justify-end min-w-0">
                        <div className="hidden lg:flex items-center gap-2 flex-wrap min-w-0">
                            {navItems.map(item => renderNavButton(item, 'desktop'))}

                            {user && !user.is_guest && (
                                <div className="relative">
                                    <DropdownMenu
                                        trigger={
                                            <div className="flex items-center gap-2">
                                                <IconQuiz className="w-4 h-4" />
                                                <span>Quiz</span>
                                                {dueCount > 0 && (
                                                    <span className="flex h-4 w-4 md:h-5 md:w-5 items-center justify-center rounded-full bg-red-500 text-[10px] text-white ring-2 ring-white dark:ring-[#101419]">
                                                        {formatCount(dueCount)}
                                                    </span>
                                                )}
                                            </div>
                                        }
                                        items={quizMenuItems}
                                        position="bottom-right"
                                    />
                                </div>
                            )}
                        </div>
                        <div className="hidden lg:flex items-center gap-2 pl-3 ml-3 border-l border-gray-200 dark:border-gray-800">
                            {/* Weather Widget */}
                            <WeatherWidget />

                            {/* Theme Toggle */}
                            <button
                                type="button"
                                onClick={toggleTheme}
                                className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold text-gray-700 dark:text-gray-200 hover:bg-plantlix-500/10 dark:hover:bg-plantlix-900/20 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-plantlix-500/60"
                                title={theme === 'dark' ? 'Zum hellen Modus wechseln' : 'Zum dunklen Modus wechseln'}
                            >
                                {theme === 'dark' ? <IconSun className="text-plantlix-500 dark:text-plantlix-400" /> : <IconMoon className="text-plantlix-500 dark:text-plantlix-400" />}
                                <span>{theme === 'dark' ? 'Hell' : 'Dunkel'}</span>
                            </button>

                            {/* User Dropdown */}
                            <DropdownMenu
                                trigger={
                                    user?.is_guest ? (
                                        <div className="flex items-center gap-2">
                                            <LeafIcon color="currentColor" className="w-4 h-4 flex-shrink-0" />
                                            <span>{userProfile?.greeting || 'Gude'}, Gast 👋</span>
                                        </div>
                                    ) : user ? (
                                        <div className="flex items-center gap-2">
                                            <IconProfile className="w-4 h-4 flex-shrink-0" />
                                            <span className="truncate max-w-[150px]">
                                                {userProfile?.greeting || 'Gude'}, {userProfile?.first_name || user?.first_name || user?.username || 'Du'}!
                                            </span>
                                        </div>
                                    ) : (
                                        <div className="flex items-center gap-2">
                                            <IconProfile className="w-4 h-4 flex-shrink-0" />
                                            <span>Menü</span>
                                        </div>
                                    )
                                }
                                items={userMenuItems}
                                position="bottom-right"
                                showChevron={true}
                            />
                        </div>

                        {/* Mobile: Compact Weather Pill + Hamburger */}
                        <div className="lg:hidden flex items-center gap-2">
                            <WeatherWidget compact />
                            <button
                                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-plantlix-500/60"
                            >
                                <svg className="w-6 h-6 text-plantlix-500 dark:text-plantlix-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    {mobileMenuOpen ? (
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                    ) : (
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
                                    )}
                                </svg>
                            </button>
                        </div>
                    </div>
                </div>

                {/* Mobile Menu */}
                {mobileMenuOpen && (
                    <div className="lg:hidden absolute left-0 right-0 top-full bg-white dark:bg-[#0a0a0a] border-b border-gray-100 dark:border-white/5 shadow-xl max-h-[calc(100dvh-70px)] overflow-y-auto overscroll-contain">
                        <div className="p-4 space-y-1 pb-24">

                            {/* Weather Entry im Mobile-Menü */}
                            <div className="mb-2 pb-2 border-b border-gray-100 dark:border-gray-800">
                                <div className="px-2 py-1 text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">Wetter</div>
                                <div onClick={() => { closeNav(); }} className="w-full">
                                    <WeatherWidget showInMenu />
                                </div>
                            </div>

                            {/* Main nav items */}
                            {navItems.map(item => renderNavButton(item, 'mobile'))}

                        {/* Quiz Section */}
                        {user && !user.is_guest && (
                            <div className="border-t border-gray-100 dark:border-gray-800 pt-2 mt-2">
                                <div className="px-4 py-2 text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                    Quiz-Modi
                                </div>
                                {quizMenuItems.map((item, index) => {
                                    if (item.type === 'divider') return null;
                                    return (
                                        <button
                                            key={item.key || index}
                                            onClick={() => {
                                                if (item.onClick) {
                                                    item.onClick();
                                                } else if (item.path) {
                                                    navigate(item.path);
                                                }
                                                setMobileMenuOpen(false);
                                            }}
                                            className="w-full px-4 py-3 rounded-lg text-sm font-medium transition text-left flex items-center gap-3 text-gray-600 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-[#1e1e1e]"
                                        >
                                            {item.icon && <item.icon className="w-5 h-5" />}
                                            <span className="flex-1">{item.label}</span>
                                            {item.badge && (
                                                <span className="flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white">
                                                    {item.badge}
                                                </span>
                                            )}
                                        </button>
                                    );
                                })}
                            </div>
                        )}

                        {/* Theme Toggle */}
                        <button
                            onClick={() => { toggleTheme(); setMobileMenuOpen(false); }}
                            className="w-full px-4 py-3 rounded-lg text-sm font-medium transition text-left flex items-center gap-2 text-gray-600 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-[#1e1e1e]"
                        >
                            {theme === 'dark' ? (
                                <>
                                    <IconSun className="text-plantlix-500 dark:text-plantlix-400" />
                                    <span>Hell</span>
                                </>
                            ) : (
                                <>
                                    <IconMoon className="text-plantlix-500 dark:text-plantlix-400" />
                                    <span>Dunkel</span>
                                </>
                            )}
                        </button>

                        {/* User Menu Section */}
                        <div className="border-t border-gray-100 dark:border-gray-800 mt-2 pt-2">
                            <div className="px-4 py-2 text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                {user?.is_guest ? 'Menü' : (userProfile?.first_name || user?.first_name || user?.username || 'Menü')}
                            </div>
                            {userMenuItems.map((item, index) => {
                                if (item.type === 'divider') return null;
                                const isLogout = item.key === 'logout';
                                return (
                                    <button
                                        key={item.key || index}
                                        onClick={() => {
                                            if (item.disabled) return;
                                            if (item.onClick) {
                                                item.onClick();
                                            } else if (item.path) {
                                                navigate(item.path);
                                            }
                                            setMobileMenuOpen(false);
                                        }}
                                        title={item.tooltip}
                                        disabled={item.disabled}
                                        className={`w-full px-4 py-3 rounded-lg text-sm font-medium transition text-left flex items-center gap-3 ${
                                            item.disabled
                                            ? 'opacity-50 cursor-not-allowed text-gray-400 dark:text-gray-500'
                                            : isLogout
                                            ? 'text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20'
                                            : 'text-gray-600 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-[#1e1e1e]'
                                            }`}
                                    >
                                        {item.icon && (
                                            <item.icon className={`w-5 h-5 ${
                                                item.disabled ? 'text-gray-400 dark:text-gray-600' :
                                                isLogout ? 'text-red-500' : ''
                                            }`} />
                                        )}
                                        <span className="flex-1">{item.label}</span>
                                        {item.badge && (
                                            <span className="flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white">
                                                {item.badge}
                                            </span>
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                        </div>
                    </div>
                )}
            </nav>
        </header>
    );
};

export default Header;
